// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { Belt, Finding, GbnfResult, TargetId, TranspileResult } from '../types';
import { toCsv } from './csv';
import { generateChecks, planEnums, planFromDrops, planOneOf } from './checks';

/**
 * The artefacts.
 *
 * The hero output of this tool is not a diagnosis, it is files. A diagnosis tells you that your belt
 * is broken on three of five targets; a file is the thing you actually paste back into the repo.
 *
 * `tools.openai.json` on its own would be ambiguous, which is why there are two: the Responses API
 * takes a FLAT tool object and Chat Completions NESTS it under `function`, and they even report
 * errors at different `param` paths (`tools[0].parameters` vs `tools[0].function.parameters`). The
 * same split applies to Gemini's two mutually exclusive schema fields.
 */

export interface Artefact {
  readonly filename: string;
  readonly mime: string;
  readonly content: string;
  /** One line under the download button. */
  readonly note: string;
}

function json(v: unknown): string {
  return JSON.stringify(v, null, 2) + '\n';
}

export function buildArtefacts(
  belt: Belt,
  transpiles: readonly TranspileResult[],
  gbnf: Readonly<Record<string, GbnfResult>>,
  beltGbnf: { readonly llamacpp?: GbnfResult; readonly xgrammar?: GbnfResult },
  findings: readonly Finding[],
): Artefact[] {
  const out: Artefact[] = [];
  const byTarget = new Map(transpiles.map((t) => [t.target, t]));

  const named = (target: TargetId): { name: string; description?: string; schema: unknown }[] => {
    const tr = byTarget.get(target);
    if (!tr) return [];
    return (tr.output as { name: string; description?: string; schema: unknown }[]) ?? [];
  };

  // ── OpenAI, both shapes ──────────────────────────────────────────────────
  const oai = named('openai-strict');
  if (oai.length > 0) {
    out.push({
      filename: 'tools.openai.responses.json',
      mime: 'application/json',
      content: json(
        oai.map((t) => ({
          type: 'function',
          name: t.name,
          ...(t.description ? { description: t.description } : {}),
          parameters: t.schema,
          strict: true,
        })),
      ),
      note: 'The flat Responses shape. `strict: true` is set explicitly — a schema that merely could be strict is not.',
    });
    out.push({
      filename: 'tools.openai.chat.json',
      mime: 'application/json',
      content: json(
        oai.map((t) => ({
          type: 'function',
          function: {
            name: t.name,
            ...(t.description ? { description: t.description } : {}),
            parameters: t.schema,
            strict: true,
          },
        })),
      ),
      note: 'The nested Chat Completions shape. Same schemas, different envelope — and a different error path if it fails.',
    });
  }

  // ── Anthropic ────────────────────────────────────────────────────────────
  const ant = named('anthropic-strict');
  if (ant.length > 0) {
    out.push({
      filename: 'tools.anthropic.json',
      mime: 'application/json',
      content: json(
        ant.map((t) => ({
          name: t.name,
          ...(t.description ? { description: t.description } : {}),
          input_schema: t.schema,
          strict: true,
        })),
      ),
      note: 'Anthropic keeps optional properties and `default`, and loses every numeric and string bound.',
    });
  }

  // ── Gemini, both fields (they are mutually exclusive, so two files) ───────
  const gem = named('gemini-parameters');
  if (gem.length > 0) {
    out.push({
      filename: 'tools.gemini.json',
      mime: 'application/json',
      content: json([
        {
          functionDeclarations: gem.map((t) => ({
            name: t.name,
            ...(t.description ? { description: t.description } : {}),
            parameters: t.schema,
          })),
        },
      ]),
      note: 'The OpenAPI subset. Works on both v1beta and v1 (GA), and is the lossiest leg.',
    });
  }
  const gemJs = named('gemini-jsonschema');
  if (gemJs.length > 0) {
    out.push({
      filename: 'tools.gemini.jsonschema.json',
      mime: 'application/json',
      content: json([
        {
          functionDeclarations: gemJs.map((t) => ({
            name: t.name,
            ...(t.description ? { description: t.description } : {}),
            parametersJsonSchema: t.schema,
          })),
        },
      ]),
      note: 'v1beta only — this field does not exist in Gemini API v1. Setting both fields on one declaration is a 400.',
    });
  }

  // ── The grammars ─────────────────────────────────────────────────────────
  const llama = beltGbnf.llamacpp;
  if (llama && llama.grammar) {
    out.push({
      filename: 'tools.gbnf',
      mime: 'text/plain',
      content: llama.grammar,
      note: 'For llama.cpp. Note `--grammar-file` installs a NON-lazy grammar: it forces a tool call from the first token and forbids prose.',
    });
  }
  const xg = beltGbnf.xgrammar;
  if (xg && xg.grammar) {
    out.push({
      filename: 'tools.xgrammar.gbnf',
      mime: 'text/plain',
      content: xg.grammar,
      note: "For vLLM's xgrammar backend, via structured_outputs.grammar. llama.cpp's own output does not parse there — this one does.",
    });
  }

  // ── The runtime checks ───────────────────────────────────────────────────
  const strictMissing = belt.tools.filter((t) => t.strict !== true).map((t) => t.name);
  const checks = generateChecks(belt, 'gbnf-llamacpp', planFromDrops(belt, gbnf), {
    strictMissing,
    oneOf: planOneOf(belt),
    enums: planEnums(belt),
  });
  out.push({
    filename: 'post-parse-checks.ts',
    mime: 'text/plain',
    content: checks.code,
    note:
      `${checks.count} mechanical assertion${checks.count === 1 ? '' : 's'}` +
      (checks.manual > 0 ? `, plus ${checks.manual} that can only be checked by hand.` : '.') +
      ' Every one is a constraint the decoder did not enforce.',
  });

  // ── The audit trail ──────────────────────────────────────────────────────
  out.push({
    filename: 'tool-audit.csv',
    mime: 'text/csv',
    content: toCsv(
      ['tool', 'target', 'severity', 'keyword', 'pointer', 'status', 'message', 'provider_error', 'provenance', 'fix'],
      findings.map((f) => [
        f.tool,
        f.target,
        f.severity,
        f.keyword,
        f.pointer || '(root)',
        f.status,
        f.message,
        f.providerError ?? '',
        f.provenance,
        f.fix ?? '',
      ]),
    ),
    note: 'One row per finding. Control characters are escaped so one finding is always one physical line.',
  });

  // ── Renames, only when there were any ────────────────────────────────────
  const renames = transpiles.flatMap((t) => t.renames.map((r) => [t.target, r.from, r.to, r.reason] as const));
  if (renames.length > 0) {
    out.push({
      filename: 'tool-name-map.csv',
      mime: 'text/csv',
      content: toCsv(['target', 'original_name', 'emitted_name', 'reason'], renames.map((r) => [...r])),
      note: 'A rename breaks the linkage between a tool call and its result. Map them back on the way in.',
    });
  }

  return out;
}

/** One pasteable text bundle, for the common case of dropping everything into a PR comment. */
export function bundle(artefacts: readonly Artefact[]): string {
  const parts: string[] = [
    '# Toolwright output',
    '',
    'Generated entirely in the browser at https://toolwright.benrichardson.dev — nothing was uploaded.',
    '',
  ];
  for (const a of artefacts) {
    parts.push(`${'='.repeat(76)}`);
    parts.push(`FILE: ${a.filename}`);
    parts.push(`${a.note}`);
    parts.push(`${'='.repeat(76)}`);
    parts.push('');
    parts.push(a.content.trimEnd());
    parts.push('');
  }
  return parts.join('\n');
}
